import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CategoryPicker } from "@/components/husholdning/CategoryPicker";
import { todayIso } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

const EMPTY = { kind: "utgift", date: todayIso(), amount: "", category_id: null, description: "", counterparty: "", note: "" };

const parseAmount = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));

/**
 * Dialog for manuell post (ny/rediger). `entry` = eksisterende post eller null. `defaults` kan sette kind/dato/kategori.
 * onSaved(entry) kalles etter lagring.
 */
export const EntryDialog = ({ open, onOpenChange, plan, entry, defaults, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) setForm({ kind: entry.kind, date: entry.date, amount: String(entry.amount).replace(".", ","), category_id: entry.category_id || null, description: entry.description || "", counterparty: entry.counterparty || "", note: entry.note || "" });
    else {
      const d = Object.fromEntries(Object.entries(defaults || {}).filter(([, v]) => v !== undefined && v !== null));
      setForm({ ...EMPTY, date: todayIso(), ...d });
    }
  }, [open, entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const amount = parseAmount(form.amount);
  const valid = form.description.trim().length > 0 && Number.isFinite(amount) && amount > 0 && /^\d{4}-\d{2}-\d{2}$/.test(form.date);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!valid || saving) return;
    setSaving(true);
    try {
      const payload = { kind: form.kind, date: form.date, amount: Math.round(amount * 100) / 100, category_id: form.category_id || null, description: form.description.trim(), counterparty: form.counterparty.trim() || null, note: form.note.trim() || null };
      const r = entry ? await api.put(`/household/entries/${entry.id}`, payload) : await api.post("/household/entries", payload);
      toast.success(entry ? "Posten er oppdatert" : `${form.kind === "inntekt" ? "Inntekt" : "Utgift"} lagt til`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="household-entry-dialog">
        <DialogHeader>
          <DialogTitle>{entry ? "Rediger post" : "Ny post"}</DialogTitle>
          <DialogDescription>Før lønn, dagligvarer, kontantkjøp og annet som ikke kommer inn via Regninger eller Faktura.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Tabs value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v, category_id: null }))}>
            <TabsList className="grid grid-cols-2 w-full" data-testid="household-entry-kind">
              <TabsTrigger value="utgift" data-testid="household-entry-kind-utgift">Utgift</TabsTrigger>
              <TabsTrigger value="inntekt" data-testid="household-entry-kind-inntekt">Inntekt</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="he-amount">Beløp (kr)</Label>
              <Input id="he-amount" inputMode="decimal" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0,00" className="tnum" autoFocus data-testid="household-entry-amount" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="he-date">Dato</Label>
              <Input id="he-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="household-entry-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="he-desc">Beskrivelse</Label>
            <Input id="he-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={form.kind === "inntekt" ? "F.eks. Lønn oktober" : "F.eks. Dagligvarer uke 42"} maxLength={120} data-testid="household-entry-description" />
          </div>
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <CategoryPicker plan={plan} kind={form.kind} value={form.category_id} onChange={(v) => setForm((f) => ({ ...f, category_id: v }))} noneLabel="Uten kategori" testId="household-entry-category" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="he-cp">{form.kind === "inntekt" ? "Fra (valgfritt)" : "Betalt til (valgfritt)"}</Label>
              <Input id="he-cp" value={form.counterparty} onChange={(e) => setForm((f) => ({ ...f, counterparty: e.target.value }))} placeholder={form.kind === "inntekt" ? "Arbeidsgiver" : "Butikk / mottaker"} maxLength={120} data-testid="household-entry-counterparty" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="he-note">Notat (valgfritt)</Label>
              <Textarea id="he-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={1} maxLength={500} className="min-h-[40px]" data-testid="household-entry-note" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="household-entry-cancel">Avbryt</Button>
            <Button type="submit" disabled={!valid || saving} data-testid="household-entry-save">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} {entry ? "Lagre" : "Legg til"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
